<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Event Pool"
  model-name="eventPool"
  param="eventPoolId"
  page-prefix="/blade/event-pools"
  :create="createEventPool"
  :fetch="fetchEventPool"
  :fetch-types="getEventPoolTypes"
  :update="updateEventPool"
  :remove="deleteEventPool"
  :enum-names="['teamDecisions']"
  :stub="eventPool"
  :validator="validator"
  :utils="utils">
<template slot-scope="{ model: eventPool, enums: { teamDecisions }, utils: { unused }, validator }">
  <text-input label v-model.trim="eventPool.name" :validator="validator.name" required autofocus>Name</text-input>

  <text-input label v-model.trim="eventPool.description" textarea>Description</text-input>

  <template v-if="eventPool && eventPool._id">
    <div class="form-row">
      <text-input
        class="col-sm-5"
        label
        v-model.trim="eventPool.lastSyncedSheetId"
      >Google Sheet ID (optional, used for bulk import)</text-input>
      <a
        v-if="eventPool.lastSyncedSheetId"
        class="btn"
        style="height: 38px; margin: 32px 4px 0 4px;"
        :href="`https://docs.google.com/spreadsheets/d/${eventPool.lastSyncedSheetId}`"
        target="_blank"
      >Open</a>
      <text-input
        class="col-sm-2"
        label
        v-model.trim="eventPool.lastSyncedSection"
        no-count
      >Section (optional)</text-input>
      <button
        class="btn"
        style="height: 38px; margin: 32px 0 0 4px;"
        :disabled="!(eventPool.lastSyncedSheetId)"
        @click.prevent="confirmAndSyncWithGoogleSheets(eventPool)"
      >Sync And Update</button>
      <div
        class="col-sm-2"
        style="
          display: flex;
          flex-flow: column nowrap;
          justify-content: end;
          align-items: start;
          padding: 16px;
          font-size: 80%;
        "
      >
        <div>Last Synced With Sheet:</div>
        <div>
          {{ (eventPool && eventPool.lastSyncedTimestamp) ? (new Date(eventPool.lastSyncedTimestamp)).toLocaleString() : 'Never' }}
        </div>
      </div>
    </div>
  </template>

  <number-input
    label
    v-model="eventPool.sampleLimit"
  >Sample Limit (set to 0 for no limit; pool considered empty after this many random events have been selected)</number-input>


  <collapsible-section
    name="Click to see explanation for Random Events"
    id="explain"
  >
    For all events to be equally likely, just set them all to the same Chance value (e.g. 1).
    <br><br>
    Any events with the First & Ordered flag will be executed in order before any other random events.  For example, if you
    have 5 events where 1-3 have a Chance of 1 and 4-5 have the First & Ordered flag set, the order will be event 4, event 5,
    and then 1-3 in a random order.  Note that these ordered events still count against the Sample Limit, so if the Sample Limit
    was 3 in this example, events 4 and 5 would be executed, followed by a single random event from 1-3.
    <br><br>
    If this event pool is used by a Section:
    <br>
    After the Section Intro, it will select an event from this list.
    When an event is selected, the starting decision is played
    and it will continue through the chain of decisions from there until there's no next decision.
    At that point, if Ends Section is checked on the decision, it will progress to the next Section,
    otherwise it will return to this event pool to randomly select another starting decision.
    Unless an event is marked repeatable, it will only ever be selected once.
    When the event pool is empty or the sample limit (above) is hit, that will end the Section.
    <br><br>
    If this event pool is used as an Interrupting Event Pool for a Gamulation:
    <br>
    Events in this pool will be used at random to insert a chain of decisions into the regular flow of decisions,
    based on the configuration in the Gamulation form.
    When an event is selected, the starting decision is played
    and it will continue through the chain of decisions from there until there's no next decision.
    At that point, if Ends Section is checked on the decision, it will progress to the next Section,
    otherwise it will return to the decision in the regular flow that was interrupted.
    Unless an event is marked repeatable, it will only ever be selected once.
    When the event pool is empty or the sample limit (above) is hit, it will no longer be able to interrupt.
  </collapsible-section>

  <array-input
    :items="eventPool.events"
    :disabled="validator.events.$invalid"
    :hide-button="eventPool.events.length >= teamDecisions.length"
  >
    <h5 slot="title">Random Events</h5>

    <div class="form-row" slot="item" slot-scope="{ item: randomEvent, index: i }">
      <select-input
        class="col-sm-6"
        label
        v-model="randomEvent.startingTeamDecision"
        :validator="validator.events.$each[i].startingTeamDecision"
        :options="teamDecisions"
        :filter="usedTeamDecisions"
        optimized
        use="_id"
        :display="td => { return `${td.name} (${td.updated})`; }"
        required
        autofocus
        :to="(id) => { return `/blade/major-decisions/${id}/edit`; }"
      >Starting Major Decision</select-input>

      <number-input
        class="col-sm-3"
        label
        v-model="randomEvent.chance"
        :validator="validator.events.$each[i].chance"
        required
        :remove="() => { eventPool.events.splice(i, 1); }"
      >Chance (0 - 1)</number-input>

      <checkbox-input
        class="col-sm-1"
        style="margin-top: 30px;"
        v-model="randomEvent.firstAndOrdered"
      >First & Ordered</checkbox-input>

      <checkbox-input
        class="col-sm-1"
        style="margin-top: 30px;"
        v-model="randomEvent.repeatable"
      >Repeatable</checkbox-input>
    </div>
    <span slot="button">Add Random Event</span>
  </array-input>
</template>
</edit-page>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';

import { nonZero } from '../../../utils/validators';
import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import NumberInput from '../../ui/inputs/NumberInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';

export default {
  components: {
    EditPage,
    CollapsibleSection,
    ArrayInput,
    NumberInput,
    SelectInput,
    TextInput,
    CheckboxInput
  },
  data() {
    return {
      fetchedEventPool: null,
      utils: {
        unused
      },
      eventPool: {
        _id: '',
        name: '',
        description: '',
        events: []
      },
      validator: {
        name: { required },
        events: {
          $each: {
            startingTeamDecision: { required },
            chance: { required, nonZero },
          }
        },
      }
    };
  },
  computed: {
    usedTeamDecisions() {
      return this.fetchedEventPool ? new Set(this.fetchedEventPool.events.map(e => e.startingTeamDecision)) : null;
    },
  },
  methods: {
    ...mapActions([
      'flashDanger',
      'getEventPool',
      'getEventPoolTypes',
      'createEventPool',
      'updateEventPool',
      'syncEventPoolWithGoogleSheet',
      'deleteEventPool'
    ]),
    async confirmAndSyncWithGoogleSheets(eventPool) {
      // eslint-disable-next-line no-alert
      if (confirm('Sync this Event Pool with Google Sheet?')) {
        try {
          await this.syncEventPoolWithGoogleSheet(eventPool);
          this.$router.go(0);
        } catch (err) {
          const message =
            (err && err.response && err.response.data)
              ? `Sheet Sync Unsuccessful: ${err.response.data}`
              : 'Sheet Sync Unsuccessful';
          this.flashDanger({ message });
          console.error(Object.assign({}, err));
        }
      }
    },
    async fetchEventPool(arg) {
      const resp = await this.getEventPool(arg);
      if (resp.eventPool) {
        // Caching for profile initialization
        this.fetchedEventPool = resp.eventPool;
      }
      return resp;
    },
  }
};
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/EventPoolEdit.vue